/* eslint-disable import/prefer-default-export */
import queryString from 'query-string';
import { generatePath } from 'react-router-dom';

import { SPEND_DEFERRED } from '../../constants/ach/apiUrls';
import {
  API_COPY_LINE_ITEM_URL,
  API_DAILY_LINE_ITEM_BUDGET_URL,
  API_DAILY_LINE_ITEM_SPEND_URL,
  API_DAILY_LINE_ITEM_SPEND_V2_URL,
  API_LINE_ITEM_NEW_URL,
  API_LINE_ITEM_STATUS_URL,
  API_LINE_ITEM_URL,
  API_LINE_ITEMS_URL,
  API_PASTE_LINE_ITEM_URL,
  API_VERIONS_LINE_ITEMS_URL,
  API_VERIONS_META_LINE_ITEMS_URL,
} from '../../constants/apiUrls';
import { getRequestJSON, postRequestJSON } from '../../util/services';

interface QueryParams {
  page?: number;
  perPage?: number;
}

export const getAll = (apiHost: string, contractId: number, { page, perPage }: QueryParams): Promise<any> => {
  const queryParams = `?${queryString.stringify({
    page: page + 1,
    per_page: perPage,
    // ACH-bj8d: skip the inline BigQuery spend read (only total_impressions, which no
    // consumer of this index reads). Omitted when SPEND_DEFERRED is undefined.
    spend: SPEND_DEFERRED,
  })}`;
  return getRequestJSON(
    apiHost,
    generatePath(API_LINE_ITEMS_URL, { contractId: contractId.toString(), '*': queryParams })
  );
};

export const getNew = (apiHost: string, contractId: number): Promise<any> =>
  getRequestJSON(apiHost, generatePath(API_LINE_ITEM_NEW_URL, { contractId: contractId.toString() }));

export const get = (apiHost: string, lineItemId: number): Promise<any> =>
  getRequestJSON(apiHost, generatePath(API_LINE_ITEM_URL, { lineItemId: lineItemId.toString(), '*': undefined }));

export const getDailySpend = (apiHost: string, lineItemId: number): Promise<any> =>
  getRequestJSON(apiHost, generatePath(API_DAILY_LINE_ITEM_SPEND_URL, { lineItemId: lineItemId.toString() }));

// V2: daily spend sourced from BigQuery (daily_pacing_output). Each row carries
// date/impressions/clicks plus `budget` (= target_spend) for the Impression Goal column.
export const getDailySpendV2 = (apiHost: string, lineItemId: number): Promise<any> =>
  getRequestJSON(apiHost, generatePath(API_DAILY_LINE_ITEM_SPEND_V2_URL, { lineItemId: lineItemId.toString() }));

// Delivery-status signals for one grain, as rendered by Admin::InsertionOrdersController#status_fields
// (ventura). last_spent_at is an ISO8601 string, or null when there is no spend in the recent
// (2-day) window the recency signals cover.
export interface LineItemStatusMetrics {
  impressions: number;
  clicks: number;
  has_impressions: boolean;
  has_clicks: boolean;
  spent_last_hour: boolean;
  spent_last_24_hours: boolean;
  last_spent_at: string | null;
}

// Admin::InsertionOrdersController#status response. `io` is the line-item (IO) total and the
// authoritative one — it comes from daily_pacing_output, the same source as the rest of the app's
// pacing numbers. `campaigns` is a per-AdGroup split read from a different BigQuery table, so it is
// NOT a decomposition of `io` and must never be summed into a line-item total; nothing renders it
// today. `source` is 'bigquery' — the endpoint replaced the DynamoDB reads (ADOPS-2733).
export interface LineItemStatus {
  insertion_order_id: number;
  impression_budget: number;
  io: LineItemStatusMetrics;
  campaigns: (LineItemStatusMetrics & { campaign_id: number })[];
  source: string;
}

export const getLineItemStatus = (apiHost: string, lineItemId: number): Promise<LineItemStatus> =>
  getRequestJSON(
    apiHost,
    generatePath(API_LINE_ITEM_STATUS_URL, { lineItemId: lineItemId.toString() })
  ) as Promise<LineItemStatus>;

export const getDailyBudget = (apiHost: string, lineItemId: number): Promise<any> =>
  getRequestJSON(apiHost, generatePath(API_DAILY_LINE_ITEM_BUDGET_URL, { lineItemId: lineItemId.toString() }));

export const getVersions = (
  apiHost: string,
  lineItemId: number,
  page: number,
  perPage: number,
  filters: Object = {}
): Promise<any> =>
  new Promise((resolve, reject) => {
    const queryParams = `?${queryString.stringify({
      ...filters,
      page: page + 1,
      per_page: perPage.toString(),
    })}`;
    const getVersionsAsyncHelper = async () => {
      const versions = await getRequestJSON(
        apiHost,
        generatePath(API_VERIONS_LINE_ITEMS_URL, {
          lineItemId: lineItemId.toString(),
          '*': queryParams,
        })
      );

      const meta = await getRequestJSON(
        apiHost,
        generatePath(API_VERIONS_META_LINE_ITEMS_URL, {
          lineItemId: lineItemId.toString(),
        })
      );

      if (versions && meta) {
        resolve({
          ...versions,
          meta,
        });
      } else {
        reject();
      }
    };

    getVersionsAsyncHelper();
  });

export const copy = (apiHost: string, lineItemId: number, selectedContractId: string): Promise<any> => {
  let queryParams = '';
  if (selectedContractId != null) {
    queryParams = `?${queryString.stringify({
      new_contract_id: selectedContractId,
    })}`;
  }
  return getRequestJSON(
    apiHost,
    generatePath(API_COPY_LINE_ITEM_URL, { lineItemId: lineItemId.toString(), '*': queryParams })
  );
};

export const paste = (apiHost: string, lineItemId: number, payload): Promise<any> =>
  postRequestJSON(apiHost, generatePath(API_PASTE_LINE_ITEM_URL, { lineItemId: lineItemId.toString() }), payload);
